import { Card } from "@/components/ui/card";
import { Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { situacaoMap, situacaoColors } from '../utils';
import { formatYAxis, formatDataValue } from '../utils';
import { EmptyState, LoadingState } from '../shared';
import CustomTooltip from './CustomTooltip';

interface NFCeChartProps {
  data: any[];
  isFetching: boolean;
  title?: string;
}

export default function NFCeChart({
  data,
  isFetching,
  title = "Status das NFCe por Período"
}: NFCeChartProps) {
  return (
    <Card className="p-6 bg-gradient-to-br from-white to-blue-50/30">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
        <div className="flex gap-2">
          {Object.entries(situacaoMap).map(([_, situacao]) => (
            <div key={situacao} className="flex items-center gap-2 text-sm">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: situacaoColors[situacao] }}
              />
              <span className="text-gray-600 font-medium">{situacao}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full h-[450px]">
        {isFetching ? (
          <LoadingState height="h-full" />
        ) : data.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 60,
              }}
              barGap={2}
              barCategoryGap={12}
              maxBarSize={60}
            >
              <defs>
                <linearGradient id="successGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.8}/>
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0.3}/>
                </linearGradient>
                <linearGradient id="warningGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.8}/>
                  <stop offset="100%" stopColor="#f59e0b" stopOpacity={0.3}/>
                </linearGradient>
                <linearGradient id="errorGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ef4444" stopOpacity={0.8}/>
                  <stop offset="100%" stopColor="#ef4444" stopOpacity={0.3}/>
                </linearGradient>
                <linearGradient id="pendingGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6b7280" stopOpacity={0.8}/>
                  <stop offset="100%" stopColor="#6b7280" stopOpacity={0.3}/>
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="2 2"
                vertical={false}
                stroke="#E5E7EB"
                strokeOpacity={0.5}
              />
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#6B7280', fontSize: 11, fontWeight: 500 }}
                dy={8}
                angle={-45}
                textAnchor="end"
                height={70}
                minTickGap={5}
                interval={0}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#6B7280', fontSize: 11, fontWeight: 500 }}
                tickFormatter={formatYAxis}
                dx={-8}
                width={50}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: 'rgba(59, 130, 246, 0.1)', radius: 8 }}
              />
              {Object.entries(situacaoMap).map(([_, situacao]) => {
                const gradientId = situacao === 'Sucesso' ? 'successGradient' :
                                 situacao === 'Contingência' ? 'warningGradient' :
                                 situacao === 'Cancelado' ? 'errorGradient' : 'pendingGradient';

                return (
                  <Bar
                    key={situacao}
                    dataKey={situacao}
                    stackId="stack"
                    fill={`url(#${gradientId})`}
                    radius={situacao === 'Sucesso' ? [6, 6, 0, 0] : [0, 0, 0, 0]}
                    stroke={situacaoColors[situacao]}
                    strokeWidth={1}
                  >
                    <LabelList
                      dataKey={situacao}
                      position="center"
                      fill="#FFFFFF"
                      formatter={formatDataValue}
                      style={{
                        fontSize: '10px',
                        fontWeight: 700,
                        textShadow: '0px 1px 3px rgba(0, 0, 0, 0.6)'
                      }}
                    />
                  </Bar>
                );
              })}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState
            height="h-full"
            title="Nenhum dado disponível para exibição"
            description="Use os filtros acima para carregar os dados do gráfico"
          />
        )}
      </div>
    </Card>
  );
}